<script lang="ts" setup>
import { computed } from 'vue';
import { computedAsync } from '@vueuse/core';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppImg, AppLazyLoadComponent } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import type { TripSummarySegment } from '../../../models';
import {
  TripSegmentDetailType,
  isTransportSegment,
  isFlightSegment,
  isHotelSegment,
  isActivitySegment,
  ItinerarySegmentType,
} from '../../../models';

import TripSummaryCardLayout from './TripSummaryCardLayout.vue';

interface TripSummaryCardMobileProps {
  segment: TripSummarySegment;
  numberOfDestinations?: number;
  hidePrices?: boolean;
}

interface HeaderElement {
  text: string;
  type: TripSegmentDetailType;
}

const props = defineProps<TripSummaryCardMobileProps>();
const { t } = i18n.global;

const shouldShowTripSummaryPreCheckout = computedAsync<boolean>(
  () => isExperimentAsync('trip_summary_pre_checkout_experiment'),
  false
);

const imageType = computed((): 'contain' | 'cover' =>
  isTransportSegment(props.segment) ? 'contain' : 'cover'
);

const imageAlt = computed((): string => {
  if (props.segment.summaryType === ItinerarySegmentType.Flight) {
    return t('a11y.flight_carrier_logo');
  }
  if (props.segment.summaryType === ItinerarySegmentType.Ferry) {
    return t('a11y.ferry_carrier_logo');
  }
  if (props.segment.summaryType === ItinerarySegmentType.Accommodation) {
    return t('a11y.hotel_image');
  }
  if (props.segment.summaryType === ItinerarySegmentType.Activity) {
    return t('a11y.activity_image');
  }
  return t('a11y.image');
});

const headerElements = computed((): HeaderElement[] => {
  const elements: HeaderElement[] = [];

  if (isTransportSegment(props.segment)) {
    if (props.segment.carrier) {
      elements.push({
        text: props.segment.carrier,
        type: TripSegmentDetailType.Carrier,
      });
    }
    if (isFlightSegment(props.segment) && props.segment.stops) {
      elements.push({
        text: props.segment.stops,
        type: TripSegmentDetailType.Stops,
      });
    }
  }

  if (isActivitySegment(props.segment)) {
    elements.push({
      text: `${props.segment.numberOfTickets} tickets`,
      type: TripSegmentDetailType.Tickets,
    });
  }

  if (isHotelSegment(props.segment)) {
    elements.push({
      text: `${props.segment.nights} nights`,
      type: TripSegmentDetailType.Nights,
    });
  }

  return elements;
});

function getTicketsText(numberOfTickets: number): string {
  return `${numberOfTickets} ${t(
    'planner.tickets',
    numberOfTickets
  ).toLowerCase()}`;
}

function getNightsText(nights: number): string {
  return `${nights} ${t('planner.nights', nights).toLowerCase()}`;
}
</script>

<template>
  <TripSummaryCardLayout class="trip-summary-card-mobile">
    <template v-if="shouldShowTripSummaryPreCheckout" #image>
      <AppLazyLoadComponent>
        <AppImg
          v-if="props.segment.images"
          :src="props.segment?.images[0] || ''"
          :alt="imageAlt"
          :img-type="imageType"
        />
      </AppLazyLoadComponent>
    </template>
    <template #header>
      <span class="segment-title-text">
        {{ props.segment.title }}
      </span>
    </template>

    <template #content>
      <div
        v-for="(price, priceIndex) of props.segment.price"
        :key="`${priceIndex}-${price.description}-${price.value}`"
        class="segment-price"
      >
        <div class="segment-price-date">
          <span class="segment-date">
            {{ props.segment.date }}
          </span>
          <span v-if="isTransportSegment(props.segment)">
            {{ props.segment.time }}
          </span>
        </div>

        <div class="segment-price-title">
          <span class="segment-title">
            {{ price.description }}
          </span>
        </div>

        <div class="segment-price-value-container">
          <div class="segment-price-label">
            <span
              v-for="(element, index) in headerElements"
              :key="index"
              class="segment-element"
            >
              <template
                v-if="
                  element.type === TripSegmentDetailType.Tickets &&
                  isActivitySegment(props.segment)
                "
              >
                {{ getTicketsText(props.segment.numberOfTickets) }}
              </template>
              <template
                v-else-if="
                  element.type === TripSegmentDetailType.Nights &&
                  isHotelSegment(props.segment)
                "
              >
                {{ getNightsText(props.segment.nights) }}
              </template>
              <template v-else>
                {{ element.text }}
              </template>
            </span>
          </div>
          <div v-if="!hidePrices" class="segment-price-value">
            {{ price.value }}
          </div>
        </div>
      </div>
    </template>
  </TripSummaryCardLayout>
</template>

<style scoped lang="scss">
.trip-summary-card-mobile {
  width: 100%;

  .segment-element {
    @include font-label-s-light;
    white-space: nowrap;
    padding: 0 var(--spacing-8);

    &:not(:last-child) {
      border-right: 1px solid var(--color-neutral-20);
    }

    &:first-child {
      padding-left: 0;
    }

    &:last-child {
      padding-right: 0;
    }
  }

  .segment-title-text {
    @include font-label-s-bold;
  }

  .segment-price {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    .segment-price-date {
      display: flex;
      gap: var(--spacing-8);
      @include font-label-s-light;
    }

    .segment-price-title {
      .segment-title {
        @include font-label-m-light;
        display: flex;
        gap: var(--spacing-12);
      }
    }

    .segment-price-value-container {
      display: flex;
      justify-content: space-between;

      .segment-price-value {
        @include font-label-m-light;
      }

      .segment-price-label {
        @include font-label-s-light;
      }
    }
  }
}
</style>
